import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout from '../../components/MainLayout';
import AuditoriaPageView from './AuditoriaPageView';
import { AUDIT_PAGE_SIZE, IAuditLog, listAuditLogs } from '../../api/audit';
import { EUserRole } from '../../types/user';
import { RootState } from '../../state-management/store';
import { apiErrorMessage } from '../../api/catalog';
import {
	setSearchQuery,
	setSelectedEntidad,
	setSelectedRol,
	setDateRange,
	resetFilters,
} from '../../state-management/slices/AuditoriaPage';

const AuditoriaPage: React.FC = () => {
	const dispatch = useDispatch();
	const [logs, setLogs] = useState<IAuditLog[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loadError, setLoadError] = useState<string | null>(null);

	const { searchQuery, selectedEntidad, selectedRol, dateRange } = useSelector(
		(state: RootState) => state.auditoriaPage
	);

	useEffect(() => {
		let cancelled = false;
		const handle = window.setTimeout(() => {
			listAuditLogs({
				search: searchQuery,
				entity: selectedEntidad,
				role: selectedRol,
				dateRange,
				page,
				pageSize: AUDIT_PAGE_SIZE,
			})
				.then((result) => {
					if (cancelled) return;
					const lastPage = Math.max(1, Math.ceil(result.total / AUDIT_PAGE_SIZE));
					if (page > lastPage) {
						setPage(lastPage);
						return;
					}
					setLogs(result.items);
					setTotal(result.total);
					setLoadError(null);
				})
				.catch((error: unknown) => {
					if (!cancelled) setLoadError(apiErrorMessage(error));
				});
		}, searchQuery ? 300 : 0);
		return () => {
			cancelled = true;
			window.clearTimeout(handle);
		};
	}, [searchQuery, selectedEntidad, selectedRol, dateRange, page]);

	const changeSearch = (query: string) => {
		setPage(1);
		dispatch(setSearchQuery(query));
	};
	const changeEntidad = (entity: typeof selectedEntidad) => {
		setPage(1);
		dispatch(setSelectedEntidad(entity));
	};
	const changeRol = (role: EUserRole | 'all') => {
		setPage(1);
		dispatch(setSelectedRol(role));
	};
	const changeDateRange = (range: string) => {
		setPage(1);
		dispatch(setDateRange(range));
	};
	const clearFilters = () => {
		setPage(1);
		dispatch(resetFilters());
	};

	return (
		<MainLayout
			pageTitle='Auditoría del sistema'
			pageSubtitle='Registro de todas las actividades realizadas en la plataforma'
		>
			<AuditoriaPageView
				logs={logs}
				total={total}
				page={page}
				pageSize={AUDIT_PAGE_SIZE}
				onPageChange={setPage}
				loadError={loadError}
				searchQuery={searchQuery}
				onSearchChange={changeSearch}
				selectedEntidad={selectedEntidad}
				onEntidadChange={changeEntidad}
				selectedRol={selectedRol}
				onRolChange={changeRol}
				dateRange={dateRange}
				onDateRangeChange={changeDateRange}
				onResetFilters={clearFilters}
			/>
		</MainLayout>
	);
};

export default AuditoriaPage;
